import { FileSystemAdapter } from '../../src/utils/file-system-adapter.js';
import { IssueService } from '../../src/services/issue-service.js';
import { PathnameService } from '../../src/services/pathname-service.js';
import { loadLlmailConfig } from '../../src/utils/config.js';
import { logger } from '../../src/utils/logger.js';
import path from 'path';
import { FrontmatterMetadata } from '../../src/types.js';
import { ResolvedConfig } from '../../src/utils/config.js';
import { IntentService } from '../../src/services/intent-service.js';
import { FileService } from '../../src/services/file-service.js';
import { FileFormatValidator } from '../../src/validation/file-format-validator.js';
import { MetadataFormatValidator } from '../../src/validation/metadata-format-validator.js';
import { FileSystemValidator } from '../../src/validation/file-system-validator.js';
import { Command } from 'commander';

export interface MoveOptions {
  // Core options (from v2)
  id?: string;
  active?: boolean;
  state?: string;
  target_state?: string;
  
  // Path options (from move.ts)
  sourcePath?: string;
  targetPath?: string;
}

export interface MoveResult {
  success: boolean;
  id?: string;
  path?: string;
  error?: string;
  warnings?: string[];
}

/**
 * Consolidated move implementation that handles:
 * 1. State transitions with metadata updates
 * 2. Path-based file movement
 */
export async function move(fs: FileSystemAdapter, options: MoveOptions, config: ResolvedConfig): Promise<MoveResult> {
  logger.info('Starting move operation...');
  
  // Initialize services with provided config
  const intentService = new IntentService(config);
  const pathnameService = new PathnameService(config, intentService);
  const fileService = new FileService(fs, pathnameService);
  const fileFormatValidator = new FileFormatValidator();
  const metadataValidator = new MetadataFormatValidator();
  const fileSystemValidator = new FileSystemValidator(fs);
  const issueService = new IssueService(
    pathnameService,
    fs,
    intentService,
    fileService,
    fileFormatValidator,
    metadataValidator,
    fileSystemValidator
  );

  // Handle path-based movement
  if (options.sourcePath) {
    logger.info(`Moving file by path: ${options.sourcePath}`);
    return handlePathBasedMove(fs, issueService, pathnameService, intentService, options);
  }

  // Handle ID-based movement
  if (options.id) {
    logger.info(`Looking for file with ID: ${options.id}`);
    return handleIdBasedMove(fs, issueService, pathnameService, intentService, options);
  }

  return {
    success: false,
    error: 'Must specify either state (-s), type (-t), active (-a), or inactive (-i)'
  };
}

async function handleIdBasedMove(
  fs: FileSystemAdapter,
  issueService: IssueService,
  pathnameService: PathnameService,
  intentService: IntentService,
  options: MoveOptions
): Promise<MoveResult> {
  if (!options.id) {
    return {
      success: false,
      error: 'ID is required for ID-based move'
    };
  }

  // Find the file by ID
  logger.info(`Looking for file with ID: ${options.id}`);
  const filePath = await issueService.fileMethods.findFileById(options.id);
  
  if (!filePath) {
    logger.error(`File with ID ${options.id} not found`);
    return {
      success: false,
      error: `File with ID ${options.id} not found`
    };
  }

  logger.info(`Found file at: ${filePath}`);

  return handleMove(fs, issueService, pathnameService, intentService, {
    ...options,
    sourcePath: filePath,
  });
}

async function handlePathBasedMove(
  fs: FileSystemAdapter,
  issueService: IssueService,
  pathnameService: PathnameService,
  intentService: IntentService,
  options: MoveOptions
): Promise<MoveResult> {
  try {
    if (!options.sourcePath) {
      return {
        success: false,
        error: 'sourcePath is required for path-based move'
      };
    }

    // Convert relative path to absolute path and canonicalize
    logger.info(`Resolving path: ${options.sourcePath}`);
    const canonicalPath = path.isAbsolute(options.sourcePath)
      ? options.sourcePath
      : path.join(process.cwd(), options.sourcePath);
    logger.info(`Resolved to: ${canonicalPath}`);

    // Check if source file exists and get metadata
    const metadataResult = await issueService.frontmatterMethods.getFrontmatterMetadata(canonicalPath);
    if (!metadataResult) {
      return {
        success: false,
        error: 'No frontmatter found'
      };
    }

    // Clean and validate metadata
    const validation = await issueService.frontmatterMethods.validateMetadata(metadataResult);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.errors.join(', ')
      };
    }

    // Check if target directory exists and is valid
    const targetPath = options.targetPath || '';
    const targetExists = await isValidDirectory(fs, targetPath);
    if (!targetExists) {
      return {
        success: false,
        error: 'Invalid target directory'
      };
    }

    try {
      // Parse the target path to get state information
      const targetPathInfo = pathnameService.parsePathname(targetPath);
      logger.info(`Target path info: ${JSON.stringify(targetPathInfo)}`);

      if (!targetPathInfo) {
        return {
          success: false,
          error: 'Invalid target path'
        };
      }

      // Update metadata based on target path
      const updatedMetadata: FrontmatterMetadata = {
        ...metadataResult,
        active: targetPathInfo.active ?? metadataResult.active,
        state: targetPathInfo.state
      };
      logger.info(`Updated metadata: ${JSON.stringify(updatedMetadata)}`);

      // Get the target location based on the target path
      const targetLocation = pathnameService.deriveFileLocation(updatedMetadata);
      const fullTargetPath = path.join(targetPath, targetLocation.basename);

      // Check if target file already exists
      const targetFileExists = await fs.exists(fullTargetPath);
      if (targetFileExists) {
        return {
          success: false,
          error: 'Target file already exists'
        };
      }

      // Move the file with updated metadata
      await issueService.fileMethods.moveFileWithMetadata(canonicalPath, fullTargetPath, updatedMetadata);

      return {
        success: true,
        path: fullTargetPath,
        warnings: validation.warnings
      };
    } catch (err) {
      logger.error(`Move failed: ${err instanceof Error ? err.message : String(err)}`);
      return {
        success: false,
        error: err instanceof Error ? err.message : String(err)
      };
    }
  } catch (err) {
    logger.error(`Move failed: ${err instanceof Error ? err.message : String(err)}`);
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err)
    };
  }
}

async function isValidDirectory(fs: FileSystemAdapter, dir: string): Promise<boolean> {
  if (!dir) return false;
  try {
    const stats = await fs.stat(dir);
    return stats.isDirectory();
  } catch {
    return false;
  }
}

async function handleMove(
  fs: FileSystemAdapter,
  issueService: IssueService,
  pathnameService: PathnameService,
  intentService: IntentService,
  options: MoveOptions
): Promise<MoveResult> {
  const canonicalPath = pathnameService.toCanonicalPath(options.sourcePath!);
  const metadataResult = await issueService.frontmatterMethods.getFrontmatterMetadata(canonicalPath);
  
  if (!metadataResult) {
    throw new Error(`No metadata found in file ${canonicalPath}`);
  }

  const validation = await issueService.frontmatterMethods.validateMetadata(metadataResult);
  if (!validation.valid) {
    throw new Error(`Invalid metadata: ${validation.errors.join(', ')}`);
  }

  const updatedMetadata = {
    ...metadataResult,
    state: options.target_state,
    active: options.target_state ? intentService.isActiveState(options.target_state) : metadataResult.active
  };

  if (!options.targetPath) {
    throw new Error('Target path is required');
  }

  const fullTargetPath = pathnameService.toCanonicalPath(options.targetPath);
  await issueService.fileMethods.moveFileWithMetadata(canonicalPath, fullTargetPath, updatedMetadata);

  return {
    success: true,
    path: fullTargetPath,
    warnings: validation.warnings
  };
}

async function handleStateTransition(
  fs: FileSystemAdapter,
  issueService: IssueService,
  pathnameService: PathnameService,
  intentService: IntentService,
  options: MoveOptions
): Promise<void> {
  if (!options.sourcePath) {
    throw new Error('Source path is required');
  }

  // Get current metadata
  const metadata = await issueService.frontmatterMethods.getFrontmatterMetadata(options.sourcePath);
  if (!metadata) {
    throw new Error('No metadata found');
  }

  // Update metadata based on options
  const updates: Partial<FrontmatterMetadata> = {};

  // Handle state transitions
  if (options.target_state) {
    // Set metadata.state to the requested new state and update active boolean accordingly
    if (intentService.isActiveState(options.target_state) || intentService.isInactiveReason(options.target_state)) {
      updates.state = options.target_state;
      updates.active = intentService.isActiveState(options.target_state);
    } else {
      throw new Error(`Invalid state: ${options.target_state}`);
    }
  }

  // Handle active/inactive flags for default sorting when no state is specified
  if (options.active !== undefined) {
    updates.active = options.active;
    // Clear state when explicitly setting active without a state
    if (!options.target_state) {
      updates.state = undefined;
    }
  }

  // Apply updates
  const updatedMetadata = { ...metadata, ...updates };

  // Validate the updated metadata
  const validation = await issueService.frontmatterMethods.validateMetadata(updatedMetadata);
  if (!validation.valid) {
    throw new Error(validation.errors.join(', '));
  }

  // Move the file with updated metadata
  await issueService.fileMethods.moveFileWithMetadata(options.sourcePath, options.sourcePath, updatedMetadata);
}

export class MoveCommand {
  private fs: FileSystemAdapter;
  private config: ResolvedConfig;

  constructor(fs: FileSystemAdapter, config: ResolvedConfig) {
    this.fs = fs;
    this.config = config;
  }

  async execute(args: string[]): Promise<MoveResult> {
    if (args.length < 2) {
      return {
        success: false,
        error: 'Must specify source and target paths'
      };
    }

    const [sourcePath, targetPath] = args;
    return move(this.fs, { sourcePath, targetPath }, this.config);
  }
} 